import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { handleGoogleLogin, formatApiErrorDetail } from "@/lib/googleLogin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock, Mail, Loader2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: form.email.trim(),
        password: form.password,
      });
      setUser(data);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="login-page">
      <header className="border-b border-border/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo-home">
            <img src="/mori-logo.jpg" alt="Mori" className="h-9 w-9 rounded-lg object-cover" />
            <span className="text-xl font-bold tracking-tight lowercase">mori</span>
          </Link>
          <Button variant="ghost" className="rounded-full gap-2 text-muted-foreground" onClick={() => navigate("/")} data-testid="login-back-button">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12 relative overflow-hidden">
        <div className="absolute inset-x-0 bottom-0 h-40 seigaiha-pattern opacity-60 pointer-events-none" />
        <div className="w-full max-w-md animate-fade-up relative">
          <div className="rounded-2xl border border-[#C75B28]/15 bg-card p-8 shadow-[0_10px_40px_rgba(165,70,30,0.14)]">
            <div className="h-11 w-11 rounded-xl bg-[#C75B28]/10 text-[#C75B28] flex items-center justify-center mb-5">
              <Lock className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Iniciar sesión</h1>
            <p className="text-sm text-muted-foreground mt-1.5 mb-6">
              Entra con el email y la contraseña que configuraste al aceptar tu invitación.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email" type="email" required autoFocus data-testid="login-email-input"
                  placeholder="nombre@correo.com" value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="login-password">Contraseña</Label>
                <Input
                  id="login-password" type="password" required data-testid="login-password-input"
                  placeholder="Tu contraseña" value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                />
              </div>
              {error && (
                <p className="text-sm text-destructive" data-testid="login-error">{error}</p>
              )}
              <Button type="submit" className="w-full rounded-full h-11 !bg-[#C75B28] hover:!bg-[#A5461E] !text-[#F8F5EE]" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-muted-foreground">o</span></div>
            </div>

            <Button
              variant="outline" className="w-full rounded-full h-11 gap-2" data-testid="login-google-button"
              onClick={() => handleGoogleLogin("/")}
            >
              <Mail className="h-4 w-4" /> Entrar con Google
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            ¿Aún no tienes acceso? Pide a tu administrador que te invite al proyecto.
          </p>
        </div>
      </main>
    </div>
  );
}
